/**
 * tcmCapacityLab — pure view-models for the capacity scan on /tcm-lab
 * (large-client readiness wave 3, H-8 / H-9; STATUS DO NEXT ^dn-hp3i6d).
 *
 * Platform-Admin surface, so the numbers and Microsoft's own nouns are shown
 * plainly (licence tier, allowance, instances, snapshot jobs). Two consumers:
 *   - TcmEnablementPanel's "Capacity" tile: the verdict before enabling or
 *     promoting a tenant (fits / start with a reduced profile / needs N
 *     licenses / blocked by the tenant's own monitors), with the numbers.
 *   - TcmSnapshotPanel's capture tile: the licence-aware monthly allowance and
 *     cadence the sweep scheduler actually uses, plus the reason it held the
 *     next sweep (the same server evaluation the scheduler acted on).
 *
 * Kept pure (no React) so the vitest harness can pin every string; the copy is
 * swept with findTells in the test.
 */

import type { TcmCapacityScan, TcmSnapshotQuotaSnapshot } from '@/types/tcm'

export interface CapacityLabRow {
  label: string
  value: string
}

export interface CapacityLabView {
  state: 'loading' | 'error' | 'ready'
  /** Verdict-first headline, six words or fewer. Empty while loading. */
  headline: string
  /** One supporting sentence; null when the headline says it all. */
  detail: string | null
  rows: CapacityLabRow[]
  /** Per-profile sizing rows (empty until the tenant is sized). */
  profiles: CapacityLabRow[]
}

function fmt(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '0'
  return Math.max(0, Math.trunc(n)).toLocaleString('en-US')
}

function plural(n: number, one: string, many: string): string {
  return `${fmt(n)} ${n === 1 ? one : many}`
}

function licenseRow(scan: TcmCapacityScan): string {
  const l = scan.license
  switch (l.tier) {
    case 'premium':
      return `Premium: ${plural(l.premiumLicenseCount, 'Entra ID Governance license', 'Entra ID Governance licenses')}`
    case 'basic':
      return 'Basic: Entra ID P1 or P2, no ID Governance seats'
    case 'none':
      return 'Below the license floor: no Entra ID P1 or P2'
    default:
      return 'Unread, sized as Basic'
  }
}

function otherMonitorsRow(scan: TcmCapacityScan): string {
  const m = scan.monitors
  if (!m.known) return 'Unread (Graph rejects an over-allowance monitor at create)'
  if (m.otherMonitorCount === 0) return 'None'
  const parts = [
    plural(m.otherMonitorCount, 'monitor', 'monitors'),
    plural(m.otherInstanceCount, 'instance', 'instances'),
    `${fmt(m.otherDailyEvaluations)} evaluations a day`,
  ]
  if (m.otherUnknownSizeCount > 0) {
    parts.push(`${fmt(m.otherUnknownSizeCount)} of unknown size`)
  }
  return parts.join(' · ')
}

function snapshotRow(scan: TcmCapacityScan): string {
  const s = scan.snapshots
  const used = `${fmt(s.guardianMonthToDate)} used by Guardian`
  const other = !s.known
    ? 'other jobs unread'
    : s.otherJobCountThisMonth === 0
      ? 'no other jobs'
      : `${fmt(s.otherMonthToDate)} by ${plural(s.otherJobCountThisMonth, 'other job', 'other jobs')}` +
        (s.otherUnknownSizeCount > 0 ? ` (${fmt(s.otherUnknownSizeCount)} unsized)` : '')
  return `${fmt(scan.capacity.monthlySnapshotResourceQuota)} resources a month · ${used} · ${other}`
}

function sweepRow(scan: TcmCapacityScan): string {
  const s = scan.sweep
  const cadence = s.cadenceDays === 1 ? 'once a day' : `every ${fmt(s.cadenceDays)} days`
  switch (s.state) {
    case 'exhausted':
      return 'Paused until the 1st (UTC): allowance reached'
    case 'hold':
      return 'Held until the 1st (UTC): it would pass the allowance'
    case 'warn':
      return `Runs ${cadence}, close to the allowance`
    default:
      return `Runs ${cadence}`
  }
}

function headlineFor(scan: TcmCapacityScan): { headline: string; detail: string | null } {
  const v = scan.verdict
  const profile = scan.profiles.find(p => p.profile === v.profile)?.displayName ?? v.profile ?? 'the default profile'
  switch (v.state) {
    case 'fits':
      return {
        headline: 'Default profile fits this tenant.',
        detail: `${profile} needs ${plural(v.instanceCount ?? 0, 'instance', 'instances')} of the ${fmt(v.ceiling)} left for Guardian.`,
      }
    case 'reduced':
      return {
        headline: `Start with ${profile}.`,
        detail:
          `The default profile is past the ${fmt(v.ceiling)} instances left for Guardian; ${profile} fits at ${fmt(v.instanceCount)}.` +
          (v.premiumLicensesNeeded != null && v.premiumLicensesNeeded > 0
            ? ` ${plural(v.premiumLicensesNeeded, 'more Entra ID Governance license', 'more Entra ID Governance licenses')} would fit the default.`
            : ''),
      }
    case 'needsLicenses':
      return {
        headline: `Needs ${plural(v.premiumLicensesNeeded ?? 0, 'more license', 'more licenses')}.`,
        detail:
          `${profile} is ${plural(v.instanceCount ?? 0, 'instance', 'instances')} against ${fmt(v.ceiling)} left beside the tenant's own monitors. ` +
          'Add Entra ID Governance licenses or have the customer remove a monitor.',
      }
    case 'tooLarge':
      return {
        headline: 'Trim to fit one baseline.',
        detail: `Every profile is past the ${fmt(v.ceiling)} instances left; tailor a narrower type list in the Monitors tab.`,
      }
    case 'blocked':
      return {
        headline: 'Blocked by the tenant’s own monitors.',
        detail:
          scan.monitors.totalMonitorCount >= scan.capacity.maxMonitors && !scan.monitors.guardianMonitorLive
            ? `The tenant runs ${fmt(scan.monitors.totalMonitorCount)} monitors, Microsoft's limit of ${fmt(scan.capacity.maxMonitors)}. The customer must remove one first.`
            : 'Their monitors use the whole monitoring allowance. The customer removes one or adds Entra ID Governance licenses.',
      }
    case 'unsized':
      return {
        headline: 'Size the tenant for a verdict.',
        detail: `${fmt(scan.guardianInstanceCeiling)} instances are free for Guardian's monitor today.`,
      }
    default:
      return {
        headline: 'Monitor room unread.',
        detail: 'Graph rejects an over-allowance monitor at create, so enabling stays safe.',
      }
  }
}

/** The /tcm-lab Capacity tile. */
export function describeCapacityForLab(
  scan: TcmCapacityScan | null | undefined,
  errored = false,
): CapacityLabView {
  if (errored) {
    return {
      state: 'error',
      headline: 'Capacity scan unavailable.',
      detail: 'Graph or the Container did not answer. Enabling stays safe: Graph rejects an over-allowance monitor at create.',
      rows: [],
      profiles: [],
    }
  }
  if (!scan) {
    return { state: 'loading', headline: '', detail: null, rows: [], profiles: [] }
  }

  const { headline, detail } = headlineFor(scan)
  const rows: CapacityLabRow[] = [
    { label: 'License tier', value: licenseRow(scan) },
    {
      label: 'Monitoring allowance',
      value: `${fmt(scan.capacity.dailyEvaluationBudget)} evaluations a day · ${fmt(scan.capacity.tenantInstanceCapacity)} instances across all monitors`,
    },
    { label: 'Other monitors', value: otherMonitorsRow(scan) },
    {
      label: 'Room for Guardian',
      value: `${plural(scan.guardianInstanceCeiling, 'instance', 'instances')} (one baseline holds at most ${fmt(scan.capacity.maxInstancesPerBaseline)})`,
    },
    {
      label: 'Current watch',
      value: scan.currentWatchInstanceCount == null ? 'No monitor yet' : plural(scan.currentWatchInstanceCount, 'instance', 'instances'),
    },
    { label: 'Snapshot allowance', value: snapshotRow(scan) },
    { label: 'Next sweep', value: sweepRow(scan) },
  ]

  const profiles: CapacityLabRow[] = scan.profiles
    .filter(p => p.instanceCount != null)
    .map(p => ({
      label: p.displayName,
      value:
        `${plural(p.instanceCount!, 'instance', 'instances')}, ` +
        (p.fits
          ? 'fits'
          : p.premiumLicensesNeeded == null
            ? 'past one baseline'
            : `needs ${plural(p.premiumLicensesNeeded, 'more license', 'more licenses')}`),
    }))

  return { state: 'ready', headline, detail, rows, profiles }
}

/**
 * The capture meter fed with the allowance the scheduler actually uses: the
 * licence-aware quota, both Guardian's and other jobs' extraction this month,
 * the licence-aware cadence, and the projected gate's state. Returns the
 * original snapshot untouched when the scan is missing or its job list wasn't
 * readable (so the meter never shows a half-merged picture).
 */
export function mergeLicensedAllowance(
  quota: TcmSnapshotQuotaSnapshot | undefined,
  scan: TcmCapacityScan | null | undefined,
): TcmSnapshotQuotaSnapshot | undefined {
  if (!quota || !scan || !scan.snapshots.known) return quota
  const s = scan.sweep
  const state = s.state === 'exhausted' ? 'Exhausted' : s.state === 'ok' ? 'Ok' : 'Warn'
  return {
    ...quota,
    monthToDate: s.guardianMonthToDate + s.otherMonthToDate,
    quota: s.quota,
    state,
    cadenceDays: s.cadenceDays,
  }
}

export interface SweepHoldNotice {
  title: string
  body: string
}

/**
 * The reason the scheduler held (or paused) the next sweep — the server's own
 * sentence, so the operator reads exactly what the scheduler acted on. Null
 * when the sweep may run.
 */
export function describeSweepHold(scan: TcmCapacityScan | null | undefined): SweepHoldNotice | null {
  if (!scan || !scan.sweep.held) return null
  const title = scan.sweep.state === 'exhausted' ? 'Sweeps paused for the month.' : 'Next sweep held.'
  const body =
    scan.sweep.reason ??
    'The next sweep would pass this month’s capture allowance, so it waits for the 1st (UTC).'
  return { title, body }
}

/**
 * Removals the sweep quarantined (readiness H-3). They are hidden from every
 * customer read, so this tile is where an operator sees them. Null when none
 * are held.
 */
export function describeHeldRemovals(quota: TcmSnapshotQuotaSnapshot | undefined): SweepHoldNotice | null {
  const held = quota?.openQuarantineCount ?? 0
  if (held <= 0) return null
  return {
    title: `${plural(held, 'removal', 'removals')} held for confirmation.`,
    body:
      'Guardian could not tell these from a truncated capture, so they stay out of the customer’s queue until ' +
      'the next clean capture files each one as drift, or closes it when the resource is back.',
  }
}
